import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const errorsCollection = collection(db, 'errors');

/**
 * Obtiene la IP del usuario
 * @returns {Promise<string>} IP del usuario
 */
const getUserIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error al obtener la IP:', error);
    return 'unknown';
  }
};

/**
 * Recopila información detallada del navegador y el sistema
 * @returns {Promise<Object>} Información del navegador y sistema
 */
const getBrowserInfo = async () => {
  const userAgent = navigator.userAgent;
  const language = navigator.language;
  const platform = navigator.platform;
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const colorDepth = window.screen.colorDepth;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const cookiesEnabled = navigator.cookieEnabled;
  const doNotTrack = navigator.doNotTrack;
  const online = navigator.onLine;
  const connection = navigator.connection ? {
    effectiveType: navigator.connection.effectiveType,
    downlink: navigator.connection.downlink,
    rtt: navigator.connection.rtt,
    saveData: navigator.connection.saveData
  } : null;

  // Obtener la IP del usuario
  const ip = await getUserIP();

  return {
    userAgent,
    language,
    platform,
    screenResolution: `${screenWidth}x${screenHeight}`,
    colorDepth,
    timezone,
    cookiesEnabled,
    doNotTrack,
    online,
    connection,
    ip
  };
};

/**
 * Recopila información de la sesión actual
 * @returns {Object} Información de la sesión
 */
const getSessionInfo = () => {
  return {
    url: window.location.href,
    referrer: document.referrer,
    timestamp: new Date().toISOString(),
    sessionStorage: Object.keys(sessionStorage).length,
    localStorage: Object.keys(localStorage).length
  };
};

/**
 * Registra un error en la colección de errores de Firebase
 * @param {Error} error - El error que ocurrió
 * @param {Object} context - Información adicional del contexto del error
 * @param {Object} userData - Información del usuario si está disponible
 * @returns {Promise<string>} ID del documento de error creado
 */
export const logError = async (error, context = {}, userData = null) => {
  try {
    const errorData = {
      // Información del error
      name: error?.name || 'Unknown Error',
      message: error?.message || 'No message provided',
      stack: error?.stack || '',
      code: error?.code || 'NO_CODE',
      
      // Información del contexto
      context: {
        ...context,
        errorType: error?.constructor?.name || 'Error'
      },
      
      // Información del usuario si está disponible
      userData: userData ? {
        cedula: userData.cedula || null,
        nombre: userData.nombre || null,
        email: userData.correo || null,
        telefono: userData.telefono || null
      } : null,
      
      // Información del navegador y sistema
      browserInfo: await getBrowserInfo(),
      
      // Información de la sesión
      sessionInfo: getSessionInfo(),
      
      // Metadatos
      timestamp: serverTimestamp(),
      environment: process.env.NODE_ENV || 'development',
      version: process.env.REACT_APP_VERSION || '1.0.0'
    };

    // Validación de datos antes de enviar a Firebase
    if (!errorData.name || !errorData.message) {
      console.warn('Error data missing required fields:', errorData);
    }

    const docRef = await addDoc(errorsCollection, errorData);
    return docRef.id;
  } catch (loggingError) {
    console.error('Error al registrar el error:', loggingError);
    // Intentar registrar el error de logging de forma más simple
    try {
      const fallbackError = {
        name: 'LoggingError',
        message: loggingError?.message || 'Error al registrar el error original',
        timestamp: serverTimestamp(),
        originalError: {
          name: error?.name || 'Unknown',
          message: error?.message || 'No message'
        }
      };
      await addDoc(errorsCollection, fallbackError);
    } catch (finalError) {
      console.error('Error crítico al intentar registrar el error de logging:', finalError);
    }
    return null;
  }
};

/**
 * Registra un error de compra específicamente
 * @param {Error} error - El error que ocurrió
 * @param {Object} purchaseData - Datos de la compra que falló
 * @param {Object} userData - Información del usuario
 * @returns {Promise<string>} ID del documento de error creado
 */
export const logPurchaseError = async (error, purchaseData, userData) => {
  const context = {
    type: 'purchase_error',
    purchaseData: {
      ...purchaseData
    }
  };

  return logError(error, context, userData);
};